/**
 * BookingSection — "Booking" card for Item detail page.
 *
 * When booking is OFF: simple toggle.
 * When booking is ON: embeds the full Booking Resource Config settings inline
 * (slot duration, booking limits, weekly schedule) — no separate page needed.
 *
 * Sections hidden when is_bookable=1: marketplace, bundles, variants, personalization.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useUpdateDoc } from "@/lib/hooks/useFrappeQuery";
import { toast } from "sonner";
import { SectionHeader } from "../../../SectionHeader";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useLanguage } from "@/i18n/useLanguage";
import {
  Calendar, Users, Layers,
  Check, Plus, Minus, Copy, Sun, Moon, Timer,
  Loader2, Banknote, Pencil,
} from "lucide-react";
import type { ShopifySectionWidgetProps } from "../types";
import { useItemPricingTable } from "@/features/inventory/api/pricingSummaryHooks";
import { useUpdateItemPrice } from "@/features/inventory/api/useUpdateItemPrice";
import { formatPrice } from "@/lib/format/currency";

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

type HourRow = {
  day_of_week: string;
  start_time: string;
  end_time: string;
  is_closed: 0 | 1;
  slot_price?: number;
  name?: string;
  idx?: number;
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_TH: Record<string, string> = {
  Monday: "\u0e08\u0e31\u0e19\u0e17\u0e23\u0e4c", Tuesday: "\u0e2d\u0e31\u0e07\u0e04\u0e32\u0e23", Wednesday: "\u0e1e\u0e38\u0e18", Thursday: "\u0e1e\u0e24\u0e2b\u0e31\u0e2a\u0e1a\u0e14\u0e35",
  Friday: "\u0e28\u0e38\u0e01\u0e23\u0e4c", Saturday: "\u0e40\u0e2a\u0e32\u0e23\u0e4c", Sunday: "\u0e2d\u0e32\u0e17\u0e34\u0e15\u0e22\u0e4c",
};
const DAY_SHORT: Record<string, string> = {
  Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "T",
  Friday: "F", Saturday: "S", Sunday: "S",
};
const DAY_SHORT_TH: Record<string, string> = {
  Monday: "\u0e08.", Tuesday: "\u0e2d.", Wednesday: "\u0e1e.", Thursday: "\u0e1e\u0e24.",
  Friday: "\u0e28.", Saturday: "\u0e2a.", Sunday: "\u0e2d\u0e32.",
};

const DEFAULT_START = "09:00";
const DEFAULT_END = "18:00";

const SLOT_PRESETS = [
  { value: 30, label: "30m" },
  { value: 60, label: "1hr" },
  { value: 90, label: "1.5hr" },
  { value: 120, label: "2hr" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strip tenant isolation suffix (e.g. " - D6EA") from Frappe namespaced names. */
function stripCompanySuffix(name: string): string {
  return name.replace(/ - [A-Za-z0-9]{2,6}$/, "");
}

function normalizeTime(t: string | null | undefined): string {
  if (!t) return "";
  const parts = String(t).split(":");
  if (parts.length < 2) return "";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
}

function formatDuration(mins: number, th: boolean): string {
  if (!mins) return "\u2014";
  if (mins >= 60 && mins % 60 === 0) return `${mins / 60} ${th ? "\u0e0a\u0e21." : "hr"}`;
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins} ${th ? "\u0e19\u0e32\u0e17\u0e35" : "min"}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{children}</h2>;
}

function Stepper({ value, onChange, min = 1, max = 99, label, suffix }: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  label: string;
  suffix?: string;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="h-7 w-7 rounded"
          onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}>
          <Minus className="w-3 h-3" />
        </Button>
        <span className="text-base font-semibold tabular-nums w-6 text-center text-primary">{value}</span>
        <Button variant="outline" size="icon" className="h-7 w-7 rounded"
          onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}>
          <Plus className="w-3 h-3" />
        </Button>
        {suffix && <span className="text-xs text-muted-foreground ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

function WeeklyMiniBar({ hours, th }: { hours: HourRow[]; th: boolean }) {
  return (
    <div className="flex gap-1">
      {DAYS.map((day) => {
        const row = hours.find(h => h.day_of_week === day);
        const isOpen = row && !row.is_closed;
        const isWeekend = day === "Saturday" || day === "Sunday";
        return (
          <div key={day} className={cn(
            "flex-1 flex flex-col items-center gap-1 py-2 rounded-md transition-colors",
            isOpen
              ? isWeekend ? "bg-blue-50 dark:bg-blue-950/30" : "bg-primary/[0.06]"
              : "bg-muted/40",
          )}>
            <span className={cn(
              "text-[10px] font-semibold uppercase",
              isOpen
                ? isWeekend ? "text-blue-600 dark:text-blue-400" : "text-primary"
                : "text-muted-foreground/50",
            )}>
              {th ? DAY_SHORT_TH[day] : DAY_SHORT[day]}
            </span>
            <div className={cn(
              "w-1.5 h-1.5 rounded-full",
              isOpen ? "bg-primary" : "bg-muted-foreground/20",
            )} />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function BookingSection({
  formData,
  onFieldChange,
}: ShopifySectionWidgetProps) {
  const itemName = formData?.name as string | undefined;
  const isNew = !itemName;
  const { textSize } = useDetailDensity();
  const { language } = useLanguage();
  const th = language === "th";

  const enabled = formData?.is_bookable === 1 || formData?.is_bookable === true;
  const [localEnabled, setLocalEnabled] = useState(enabled);
  const configName = (formData?.booking_config as string) || "";
  const company = (formData?.company as string) || "";

  useEffect(() => { setLocalEnabled(enabled); }, [enabled]);

  const { mutateAsync: updateItem, isPending } = useUpdateDoc("Item", itemName || "");

  // --- Config data (fetched when enabled + config linked) ---
  const [configData, setConfigData] = useState<Record<string, any> | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configs, setConfigs] = useState<Array<{ name: string; config_name: string }>>([]);
  const [configsLoading, setConfigsLoading] = useState(true);
  const { mutateAsync: updateConfig } = useUpdateDoc("Booking Resource Config", configName || "__none__");
  const [configDirty, setConfigDirty] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Fetch available configs list — filtered by company for tenant isolation
  useEffect(() => {
    if (!localEnabled) { setConfigsLoading(false); return; }
    if (!company) { setConfigs([]); setConfigsLoading(false); return; }
    let cancelled = false;
    import("@/lib/erp/client").then(({ getERPClient }) => {
      const client = getERPClient();
      client.call("frappe.client.get_list", {
        doctype: "Booking Resource Config",
        filters: { status: ["in", ["Active", "Draft"]], company },
        fields: ["name", "config_name"],
        limit_page_length: 50,
      }).then((res: any) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.message || []);
        setConfigs(list);
        setConfigsLoading(false);
      }).catch(() => { if (!cancelled) setConfigsLoading(false); });
    });
    return () => { cancelled = true; };
  }, [localEnabled, company]);

  // Fetch full config doc when config is linked
  useEffect(() => {
    if (!configName) { setConfigData(null); return; }
    let cancelled = false;
    setConfigLoading(true);
    import("@/lib/erp/client").then(({ getERPClient }) => {
      const client = getERPClient();
      client.call("frappe.client.get", {
        doctype: "Booking Resource Config",
        name: configName,
      }).then((res: any) => {
        if (cancelled) return;
        const doc = res?.message || res;
        setConfigData(doc);
        setConfigLoading(false);
      }).catch(() => {
        if (!cancelled) { setConfigLoading(false); setConfigData(null); }
      });
    });
    return () => { cancelled = true; };
  }, [configName]);

  // --- Toggle handler ---
  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const next = !localEnabled;
    setLocalEnabled(next);

    if (isNew) {
      onFieldChange("is_bookable", next ? 1 : 0);
      if (!next) onFieldChange("booking_config", "");
      return;
    }

    const prev = localEnabled;
    try {
      const patch: Record<string, unknown> = { is_bookable: next ? 1 : 0 };
      if (!next) patch.booking_config = "";
      await updateItem(patch);
      onFieldChange("is_bookable", next ? 1 : 0);
      if (!next) onFieldChange("booking_config", "");
    } catch {
      setLocalEnabled(prev);
      toast.error("Failed to update booking setting.");
    }
  };

  // --- Config picker handler ---
  const handleConfigChange = async (value: string) => {
    if (isNew) { onFieldChange("booking_config", value); return; }
    try {
      await updateItem({ booking_config: value });
      onFieldChange("booking_config", value);
    } catch {
      toast.error("Failed to update booking configuration.");
    }
  };

  // --- Config field change (local state, saved on explicit save) ---
  const handleConfigFieldChange = useCallback((fieldname: string, value: any) => {
    setConfigData(prev => prev ? { ...prev, [fieldname]: value } : prev);
    setConfigDirty(true);
  }, []);

  // --- Save config ---
  const handleSaveConfig = async () => {
    if (!configData || !configName) return;
    setSavingConfig(true);
    try {
      await updateConfig({
        slot_duration_minutes: configData.slot_duration_minutes,
        max_concurrent_bookings: configData.max_concurrent_bookings,
        min_booking_slots: configData.min_booking_slots,
        max_booking_slots: configData.max_booking_slots,
        advance_booking_days: configData.advance_booking_days,
        status: configData.status,
        operating_hours: configData.operating_hours,
      });
      setConfigDirty(false);
      toast.success(th ? "\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e41\u0e25\u0e49\u0e27" : "Booking config saved");
    } catch {
      toast.error(th ? "\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08" : "Failed to save config");
    } finally {
      setSavingConfig(false);
    }
  };

  // --- Parsed config data ---
  const slotMins = Number(configData?.slot_duration_minutes) || 60;
  const maxConcurrent = Number(configData?.max_concurrent_bookings) || 1;
  const minSlots = Number(configData?.min_booking_slots) || 1;
  const maxSlots = Number(configData?.max_booking_slots) || 4;
  const advanceDays = Number(configData?.advance_booking_days) || 30;
  const configStatus = String(configData?.status ?? "Draft");
  const isActive = configStatus === "Active";

  const operatingHours = useMemo(() => {
    const rows = (configData?.operating_hours ?? []) as HourRow[];
    const byDay = new Map(rows.map(r => [r.day_of_week, r]));
    return DAYS.map((day, i) => byDay.get(day) ?? {
      day_of_week: day, start_time: DEFAULT_START, end_time: DEFAULT_END, is_closed: 1 as const, idx: i + 1,
    });
  }, [configData?.operating_hours]);

  const updateHours = useCallback((newHours: HourRow[]) => {
    handleConfigFieldChange("operating_hours", newHours.map((h, i) => ({ ...h, idx: i + 1 })));
  }, [handleConfigFieldChange]);

  const toggleDay = useCallback((day: string) => {
    const updated = operatingHours.map(h =>
      h.day_of_week === day
        ? { ...h, is_closed: h.is_closed ? 0 as const : 1 as const, start_time: h.start_time || DEFAULT_START, end_time: h.end_time || DEFAULT_END }
        : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const updateDayTime = useCallback((day: string, field: "start_time" | "end_time", value: string) => {
    const updated = operatingHours.map(h =>
      h.day_of_week === day ? { ...h, [field]: value } : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const setWeekdayHours = useCallback((start: string, end: string) => {
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    const updated = operatingHours.map(h =>
      weekdays.includes(h.day_of_week)
        ? { ...h, is_closed: 0 as const, start_time: start, end_time: end }
        : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const copyToAll = useCallback((sourceDay: string) => {
    const source = operatingHours.find(h => h.day_of_week === sourceDay);
    if (!source) return;
    const updated = operatingHours.map(h => ({
      ...h, start_time: source.start_time, end_time: source.end_time,
      is_closed: source.is_closed, slot_price: source.slot_price,
    }));
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const updateDayPrice = useCallback((day: string, price: number) => {
    const updated = operatingHours.map(h =>
      h.day_of_week === day ? { ...h, slot_price: price } : h,
    );
    updateHours(updated);
  }, [operatingHours, updateHours]);

  const openDayCount = operatingHours.filter(h => !h.is_closed).length;

  return (
    <div>
      <SectionHeader label={th ? "\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Booking"} textSize={textSize} />

      <div className="space-y-5">
        {/* Toggle card */}
        <div className="rounded-xl border border-border shadow-sm bg-card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4">
            <div>
              <span className="text-sm font-medium text-foreground">
                {th ? "\u0e40\u0e1b\u0e34\u0e14\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Enable Booking"}
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                {th ? "\u0e41\u0e2a\u0e14\u0e07\u0e15\u0e31\u0e27\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19/\u0e40\u0e27\u0e25\u0e32\u0e1a\u0e19\u0e2b\u0e19\u0e49\u0e32\u0e23\u0e49\u0e32\u0e19" : "Show date/time picker on storefront"}
              </p>
            </div>
            <Switch
              checked={localEnabled}
              onCheckedChange={() => {}}
              onClick={handleToggle}
              disabled={isPending}
              aria-label="Enable booking"
            />
          </div>
        </div>

        {/* Expanded config when enabled */}
        {localEnabled && (
          <>
            {/* Config picker */}
            <div className="rounded-xl border border-border shadow-sm bg-card overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4">
                <span className="text-sm font-medium text-foreground">
                  {th ? "\u0e01\u0e32\u0e23\u0e15\u0e31\u0e49\u0e07\u0e04\u0e48\u0e32" : "Configuration"}
                </span>
                <div className="flex items-center gap-2">
                  {configsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : configs.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      {th ? "\u0e44\u0e21\u0e48\u0e21\u0e35\u0e01\u0e32\u0e23\u0e15\u0e31\u0e49\u0e07\u0e04\u0e48\u0e32" : "No configs available"}
                    </span>
                  ) : (
                    <select
                      value={configName}
                      onChange={(e) => handleConfigChange(e.target.value)}
                      className="text-sm bg-transparent border border-border rounded-lg px-3 py-1.5 text-right min-w-[160px] focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">{th ? "\u0e40\u0e25\u0e37\u0e2d\u0e01..." : "Select..."}</option>
                      {configs.map((c) => (
                        <option key={c.name} value={c.name}>
                          {stripCompanySuffix(c.config_name || c.name)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            {/* Inline config editor — only when a config is linked */}
            {configName && configData && !configLoading && (
              <InlineBookingConfig
                configData={configData}
                configStatus={configStatus}
                isActive={isActive}
                slotMins={slotMins}
                maxConcurrent={maxConcurrent}
                minSlots={minSlots}
                maxSlots={maxSlots}
                advanceDays={advanceDays}
                operatingHours={operatingHours}
                openDayCount={openDayCount}
                th={th}
                itemCode={itemName || ""}
                onFieldChange={handleConfigFieldChange}
                toggleDay={toggleDay}
                updateDayTime={updateDayTime}
                setWeekdayHours={setWeekdayHours}
                copyToAll={copyToAll}
                updateDayPrice={updateDayPrice}
                configDirty={configDirty}
                savingConfig={savingConfig}
                onSave={handleSaveConfig}
              />
            )}

            {configName && configLoading && (
              <div className="rounded-xl border border-border bg-card p-8 flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slot Pricing Card — reads/writes ERPNext Item Price
// ---------------------------------------------------------------------------

function SlotPricingCard({
  itemCode, slotMins, minSlots, maxSlots, th, operatingHours,
}: {
  itemCode: string;
  slotMins: number;
  minSlots: number;
  maxSlots: number;
  th: boolean;
  operatingHours: HourRow[];
}) {
  const { data: pricingData, isLoading } = useItemPricingTable(itemCode);
  const { mutateAsync: updatePrice, isPending: saving } = useUpdateItemPrice(itemCode);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const primaryRow = pricingData?.rows?.find((r) => r.is_primary);
  const basePrice = primaryRow?.base_price ?? 0;
  const currency = primaryRow?.currency ?? "THB";

  const handleStartEdit = () => {
    setEditValue(String(basePrice));
    setEditing(true);
  };

  const handleSave = async () => {
    const rate = parseFloat(editValue);
    if (isNaN(rate) || rate < 0) return;
    try {
      await updatePrice(rate);
      toast.success(th ? "บันทึกราคาแล้ว" : "Price updated");
      setEditing(false);
    } catch {
      toast.error(th ? "ไม่สามารถอัปเดตราคาได้" : "Failed to update price");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") setEditing(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border/50">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {th ? "ราคาต่อสล็อต" : "Slot Pricing"}
        </span>
      </div>

      <div className="p-5 space-y-4">
        {/* Price per slot */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Banknote className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">
                {th ? `ราคาต่อสล็อต (${slotMins} นาที)` : `Per slot (${slotMins} min)`}
              </p>
              {editing ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    className="h-8 w-32 text-sm tabular-nums"
                  />
                  <Button size="sm" variant="default" onClick={handleSave} disabled={saving} className="h-8 px-3">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-8 px-2 text-muted-foreground">
                    {th ? "ยกเลิก" : "Cancel"}
                  </Button>
                </div>
              ) : (
                <p className="text-lg font-semibold tabular-nums">
                  {isLoading ? "..." : formatPrice(basePrice, currency)}
                </p>
              )}
            </div>
          </div>

          {!editing && !isLoading && (
            <button
              type="button"
              onClick={handleStartEdit}
              className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              {th ? "แก้ไข" : "Edit"}
            </button>
          )}
        </div>

        {/* Per-day price breakdown */}
        {(() => {
          const dayPrices = operatingHours.filter((h) => !h.is_closed && h.slot_price && h.slot_price > 0);
          if (dayPrices.length === 0 && maxSlots > 1 && basePrice > 0 && !editing) {
            // No day overrides — show multi-slot preview with base price
            return (
              <div className="rounded-lg bg-muted/30 px-4 py-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {th ? "ตัวอย่างราคาตามจำนวนสล็อต" : "Price by number of slots"}
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-1">
                  {Array.from({ length: maxSlots - minSlots + 1 }, (_, i) => minSlots + i).map((n) => (
                    <div key={n} className="flex items-baseline gap-1.5">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {n} {n === 1 ? "slot" : "slots"}
                      </span>
                      <span className="text-sm font-medium tabular-nums">
                        {formatPrice(basePrice * n, currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          if (dayPrices.length > 0 && !editing) {
            // Day-specific prices exist — show per-day breakdown
            return (
              <div className="rounded-lg bg-muted/30 px-4 py-3">
                <p className="text-xs text-muted-foreground mb-2">
                  {th ? "ราคาตามวัน" : "Price by day"}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                  {operatingHours.filter((h) => !h.is_closed).map((h) => {
                    const dayPrice = h.slot_price && h.slot_price > 0 ? h.slot_price : basePrice;
                    const isOverride = h.slot_price && h.slot_price > 0;
                    return (
                      <div key={h.day_of_week} className="flex items-baseline justify-between gap-2">
                        <span className="text-xs text-muted-foreground">
                          {th ? DAY_TH[h.day_of_week] : h.day_of_week}
                        </span>
                        <span className={cn(
                          "text-sm tabular-nums",
                          isOverride ? "font-semibold text-foreground" : "text-muted-foreground",
                        )}>
                          {formatPrice(dayPrice, currency)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }
          return null;
        })()}

        {/* Source note */}
        <p className="text-[11px] text-muted-foreground/60">
          {th
            ? "ราคาเริ่มต้นจาก Item Price — ตั้งราคาแยกตามวันได้ในตารางเวลา"
            : "Default from Item Price — set day-specific prices in the schedule above"}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline Booking Config — embedded in the Item page
// ---------------------------------------------------------------------------

function InlineBookingConfig({
  configData: _configData, configStatus: _configStatus, isActive, slotMins, maxConcurrent,
  minSlots, maxSlots, advanceDays, operatingHours, openDayCount,
  th, itemCode, onFieldChange, toggleDay, updateDayTime,
  setWeekdayHours, copyToAll, updateDayPrice, configDirty, savingConfig, onSave,
}: {
  configData: Record<string, any>;
  configStatus: string;
  isActive: boolean;
  slotMins: number;
  maxConcurrent: number;
  minSlots: number;
  maxSlots: number;
  advanceDays: number;
  operatingHours: HourRow[];
  openDayCount: number;
  th: boolean;
  itemCode: string;
  onFieldChange: (field: string, value: any) => void;
  toggleDay: (day: string) => void;
  updateDayTime: (day: string, field: "start_time" | "end_time", value: string) => void;
  setWeekdayHours: (start: string, end: string) => void;
  copyToAll: (sourceDay: string) => void;
  updateDayPrice: (day: string, price: number) => void;
  configDirty: boolean;
  savingConfig: boolean;
  onSave: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Status toggle */}
      <div className={cn(
        "flex items-center justify-between rounded-xl px-5 py-4 border transition-colors",
        isActive ? "bg-primary/[0.06] border-primary/20" : "bg-muted/30 border-border",
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center",
            isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
          )}>
            <Calendar className="w-4 h-4" />
          </div>
          <p className={cn("text-sm font-medium", isActive ? "text-primary" : "text-foreground")}>
            {isActive
              ? (th ? "\u0e40\u0e1b\u0e34\u0e14\u0e43\u0e0a\u0e49\u0e07\u0e32\u0e19 \u2014 \u0e25\u0e39\u0e01\u0e04\u0e49\u0e32\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e08\u0e2d\u0e07\u0e44\u0e14\u0e49" : "Active \u2014 customers can book")
              : (th ? "\u0e09\u0e1a\u0e31\u0e1a\u0e23\u0e48\u0e32\u0e07 \u2014 \u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e40\u0e1b\u0e34\u0e14\u0e43\u0e2b\u0e49\u0e08\u0e2d\u0e07" : "Draft \u2014 not yet accepting bookings")
            }
          </p>
        </div>
        <Switch
          checked={isActive}
          onCheckedChange={(checked) => onFieldChange("status", checked ? "Active" : "Draft")}
        />
      </div>

      {/* Slot Duration */}
      <div className="space-y-2.5">
        <SectionLabel>{th ? "\u0e23\u0e30\u0e22\u0e30\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e25\u0e47\u0e2d\u0e15" : "Slot Duration"}</SectionLabel>
        <div className="flex gap-2">
          {SLOT_PRESETS.map((preset) => {
            const active = slotMins === preset.value;
            return (
              <button key={preset.value} type="button"
                onClick={() => onFieldChange("slot_duration_minutes", preset.value)}
                className={cn(
                  "relative px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                  active
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-white dark:bg-card border border-border hover:border-muted-foreground/30 text-foreground",
                )}>
                {preset.label}
                {active && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-foreground flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-primary" />
                  </div>
                )}
              </button>
            );
          })}
          {!SLOT_PRESETS.some(p => p.value === slotMins) && (
            <div className="px-4 py-2.5 rounded-lg text-sm font-medium bg-primary text-primary-foreground shadow-sm">
              {slotMins}m
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {th
            ? `\u0e41\u0e15\u0e48\u0e25\u0e30\u0e2a\u0e25\u0e47\u0e2d\u0e15 ${formatDuration(slotMins, th)} \u0e25\u0e39\u0e01\u0e04\u0e49\u0e32\u0e08\u0e2d\u0e07\u0e44\u0e14\u0e49 ${minSlots}-${maxSlots} \u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e15\u0e48\u0e2d\u0e04\u0e23\u0e31\u0e49\u0e07`
            : `Each slot is ${formatDuration(slotMins, th)}. Customers book ${minSlots}\u2013${maxSlots} consecutive slots.`
          }
        </p>
      </div>

      {/* Booking Limits */}
      <div className="space-y-2.5">
        <SectionLabel>{th ? "\u0e01\u0e32\u0e23\u0e08\u0e2d\u0e07" : "Booking Limits"}</SectionLabel>
        <div className="rounded-xl border border-border bg-white dark:bg-card shadow-sm">
          <Stepper label={th ? "\u0e08\u0e2d\u0e07\u0e1e\u0e23\u0e49\u0e2d\u0e21\u0e01\u0e31\u0e19\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14" : "Max concurrent"} value={maxConcurrent}
            onChange={(v) => onFieldChange("max_concurrent_bookings", v)} min={1} max={50} />
          <div className="border-t border-border/50 mx-4" />
          <Stepper label={th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e15\u0e48\u0e33\u0e2a\u0e38\u0e14" : "Min slots"} value={minSlots}
            onChange={(v) => onFieldChange("min_booking_slots", v)} min={1} max={maxSlots} />
          <div className="border-t border-border/50 mx-4" />
          <Stepper label={th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15\u0e2a\u0e39\u0e07\u0e2a\u0e38\u0e14" : "Max slots"} value={maxSlots}
            onChange={(v) => onFieldChange("max_booking_slots", v)} min={minSlots} max={24} />
          <div className="border-t border-border/50 mx-4" />
          <Stepper label={th ? "\u0e08\u0e2d\u0e07\u0e25\u0e48\u0e27\u0e07\u0e2b\u0e19\u0e49\u0e32" : "Advance booking"} value={advanceDays}
            onChange={(v) => onFieldChange("advance_booking_days", v)} min={1} max={365} suffix={th ? "\u0e27\u0e31\u0e19" : "days"} />
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <SectionLabel>{th ? "\u0e15\u0e32\u0e23\u0e32\u0e07\u0e40\u0e27\u0e25\u0e32" : "Weekly Schedule"}</SectionLabel>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setWeekdayHours("09:00", "18:00")}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">
              <Sun className="w-3 h-3" />
              {th ? "\u0e08.-\u0e28. 9-18" : "Mon-Fri 9-6"}
            </button>
            <button type="button" onClick={() => setWeekdayHours("10:00", "22:00")}
              className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">
              <Moon className="w-3 h-3" />
              {th ? "\u0e08.-\u0e28. 10-22" : "Mon-Fri 10-10"}
            </button>
          </div>
        </div>

        <WeeklyMiniBar hours={operatingHours} th={th} />

        <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden shadow-sm">
          {operatingHours.map((row, i) => {
            const isOpen = !row.is_closed;
            const dayLabel = th ? DAY_TH[row.day_of_week] : row.day_of_week;
            const isWeekend = row.day_of_week === "Saturday" || row.day_of_week === "Sunday";
            return (
              <div key={row.day_of_week}>
                {i > 0 && <div className="border-t border-border/50 mx-4" />}
                <div className={cn(
                  "flex items-center gap-3 px-4 py-3 transition-colors",
                  !isOpen && "bg-muted/20",
                )}>
                  <Switch checked={isOpen} onCheckedChange={() => toggleDay(row.day_of_week)} />
                  <span className={cn(
                    "text-sm font-medium w-[80px]",
                    isOpen ? "text-foreground" : "text-muted-foreground/50",
                    isWeekend && isOpen && "text-blue-600 dark:text-blue-400",
                  )}>
                    {dayLabel}
                  </span>
                  {isOpen ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input type="time"
                        value={normalizeTime(row.start_time) || DEFAULT_START}
                        onChange={(e) => updateDayTime(row.day_of_week, "start_time", e.target.value)}
                        className="h-8 w-[110px] text-[13px] font-mono tabular-nums" />
                      <span className="text-muted-foreground/40">{"\u2192"}</span>
                      <Input type="time"
                        value={normalizeTime(row.end_time) || DEFAULT_END}
                        onChange={(e) => updateDayTime(row.day_of_week, "end_time", e.target.value)}
                        className="h-8 w-[110px] text-[13px] font-mono tabular-nums" />
                      <div className="flex items-center gap-1 ml-1">
                        <Banknote className="w-3 h-3 text-muted-foreground/40" />
                        <Input type="number" min={0} step="0.01"
                          value={row.slot_price ?? ""}
                          onChange={(e) => updateDayPrice(row.day_of_week, parseFloat(e.target.value) || 0)}
                          placeholder={th ? "ค่าเริ่มต้น" : "default"}
                          className="h-8 w-[90px] text-[13px] tabular-nums" />
                      </div>
                      <button type="button" onClick={() => copyToAll(row.day_of_week)}
                        className="ml-auto p-1.5 rounded text-muted-foreground/30 hover:text-muted-foreground hover:bg-muted/40 transition-colors"
                        title={th ? "\u0e04\u0e31\u0e14\u0e25\u0e2d\u0e01\u0e44\u0e1b\u0e17\u0e38\u0e01\u0e27\u0e31\u0e19" : "Copy to all days"}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground/40 italic">
                      {th ? "\u0e1b\u0e34\u0e14\u0e17\u0e33\u0e01\u0e32\u0e23" : "Closed"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary stats */}
      <div className="rounded-xl border border-border bg-white dark:bg-card shadow-sm">
        <div className="px-4 py-2.5 border-b border-border/50">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {th ? "\u0e2a\u0e23\u0e38\u0e1b" : "Summary"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-0">
          {[
            { label: th ? "\u0e2a\u0e25\u0e47\u0e2d\u0e15" : "Slot", value: formatDuration(slotMins, th), icon: Timer },
            { label: th ? "\u0e1e\u0e23\u0e49\u0e2d\u0e21\u0e01\u0e31\u0e19" : "Concurrent", value: `${maxConcurrent}`, icon: Users },
            { label: th ? "\u0e15\u0e48\u0e2d\u0e04\u0e23\u0e31\u0e49\u0e07" : "Per booking", value: `${minSlots}\u2013${maxSlots}`, icon: Layers },
            { label: th ? "\u0e27\u0e31\u0e19\u0e40\u0e1b\u0e34\u0e14" : "Open", value: `${openDayCount}/7`, icon: Calendar },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center py-3 border-r last:border-r-0 border-border/30">
              <Icon className="w-3.5 h-3.5 text-muted-foreground mb-1" />
              <span className="text-sm font-semibold tabular-nums">{value}</span>
              <span className="text-[10px] text-muted-foreground">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Slot Pricing — reads/writes ERPNext Item Price (single source of truth) */}
      <SlotPricingCard itemCode={itemCode} slotMins={slotMins} minSlots={minSlots} maxSlots={maxSlots} th={th} operatingHours={operatingHours} />

      {/* Save button */}
      {configDirty && (
        <div className="flex justify-end">
          <Button onClick={onSave} disabled={savingConfig} className="gap-2">
            {savingConfig && <Loader2 className="w-4 h-4 animate-spin" />}
            {th ? "\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e01\u0e32\u0e23\u0e15\u0e31\u0e49\u0e07\u0e04\u0e48\u0e32" : "Save Booking Config"}
          </Button>
        </div>
      )}
    </div>
  );
}
